// Machine (press / CNC-VMC) requirement from process cycle times × monthly volume.
// Capacity assumptions:
//   Press machines : 26 days × 10 hrs = 260 machine-hours / month
//   CNC / VMC      : 26 days × 20 hrs = 520 machine-hours / month
export const PRESS_HRS_PER_MONTH = 26 * 10;    // 260
export const CNCVMC_HRS_PER_MONTH = 26 * 20;   // 520

// Classify a machine (from the process's tonnage tag) as press vs cnc/vmc.
export function machineType(tonnage) {
  const t = String(tonnage || "").toLowerCase();
  return (t.includes("cnc") || t.includes("vmc")) ? "cnc_vmc" : "press";
}

// Capacity for a specific operation machine_type (press/cnc/vmc/welding/other), falling
// back to the tonnage tag when no explicit type is set. CNC/VMC = 520 h/mo, else 260.
function capacityForProcess(machine_type, tonnage) {
  const m = String(machine_type || "").toLowerCase();
  if (m === "cnc" || m === "vmc") return CNCVMC_HRS_PER_MONTH;
  if (m) return PRESS_HRS_PER_MONTH; // press / welding / other
  return capacityFor(machineType(tonnage));
}

export function capacityFor(type) {
  return type === "cnc_vmc" ? CNCVMC_HRS_PER_MONTH : PRESS_HRS_PER_MONTH;
}

// Returns { rows:[{machine,type,hours,capacity,machines}], totalMachines, totalHours, monthlyVolume }
export function computeMachineRequirement(processes, monthlyVolume) {
  const vol = Number(monthlyVolume) || 0;
  const byMachine = {};
  (processes || []).forEach((p) => {
    const ct = Number(p.cycle_time_sec) || 0;
    if (ct <= 0) return;
    const ton = (p.tonnage || "").trim();
    // Non-machine step (Shearing/Slitting, no_machine flag, or no tonnage assigned)
    // → grouped under a visible "No machine" bucket with zero machine load.
    const isNoMachine = p.no_machine || /shearing|slitting/i.test(p.name || "") || !ton;
    if (isNoMachine) {
      if (!byMachine["No machine"]) byMachine["No machine"] = { machine: "No machine", type: "—", capacity: 0, hours: 0, noMachine: true };
      byMachine["No machine"].hours += (ct * vol) / 3600;
      return;
    }
    const key = ton;
    const mt = (p.machine_type || "").trim().toLowerCase();
    if (!byMachine[key]) byMachine[key] = { machine: key, type: mt || machineType(key), capacity: capacityForProcess(mt, key), hours: 0 };
    else if (mt && !byMachine[key]._hasType) { byMachine[key].type = mt; byMachine[key].capacity = capacityForProcess(mt, key); byMachine[key]._hasType = true; }
    byMachine[key].hours += (ct * vol) / 3600;
  });
  // Per part we show FRACTIONAL press-load (shared capacity) — e.g. 0.64 of a press —
  // NOT a rounded whole machine. Whole machines are only computed at the plant level
  // (in Reports) after summing every part's hours on that machine type.
  const rows = Object.values(byMachine)
    .map((m) => {
      if (m.noMachine) return { machine: m.machine, type: "—", hours: m.hours, capacity: 0, machines: 0 };
      const capacity = m.capacity || capacityFor(m.type);
      return { machine: m.machine, type: m.type, hours: m.hours, capacity, machines: capacity > 0 ? m.hours / capacity : 0 };
    })
    // Real machines first (by load); the "No machine" bucket always last.
    .sort((a, b) => (a.machine === "No machine" ? 1 : b.machine === "No machine" ? -1 : b.hours - a.hours));
  return {
    rows,
    totalMachines: rows.reduce((s, r) => s + r.machines, 0),
    totalHours: rows.reduce((s, r) => s + r.hours, 0),
    monthlyVolume: vol,
  };
}
